import React, { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "admin/AdminLayout";
import ConfirmDialog from "admin/ConfirmDialog";
import { listCatalogPages, addCatalogPage, deleteCatalogPage, moveCatalogPage } from "lib/catalogPages";

export default function AdminCatalog() {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const refresh = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      setPages(await listCatalogPages());
    } catch (err) {
      setLoadError(err.message || "Could not load the catalog.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setIsUploading(true);
    try {
      await addCatalogPage({ imageFile: file });
      toast.success("Catalog page added.");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not add the page.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleMove = async (id, direction) => {
    setBusyId(id);
    try {
      await moveCatalogPage(id, direction);
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not reorder pages.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteCatalogPage(deleteTarget.id);
      toast.success("Catalog page deleted.");
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not delete the page.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <ToastContainer position="bottom-right" />

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-800 sm:text-3xl">Catalog Pages</h1>
          <p className="mt-1 text-sm text-slate-500">
            These are the pages shown on the public Catalog page, in order.
          </p>
        </div>
        <button
          type="button"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 rounded-lg bg-maroon-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-maroon-800 disabled:opacity-60"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {isUploading ? "Uploading…" : "Add Page"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      )}

      {!isLoading && loadError && <p className="text-red-600">{loadError}</p>}

      {!isLoading && !loadError && pages.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-slate-400">
          No catalog pages yet — click &quot;Add Page&quot; to upload the first one.
        </p>
      )}

      {!isLoading && !loadError && pages.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {pages.map((page, index) => (
            <div key={page.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="aspect-[4/5] bg-slate-50">
                <img
                  src={page.image_url}
                  alt={`Catalog page ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
                <span className="text-xs font-medium text-slate-500">Page {index + 1}</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={index === 0 || busyId === page.id}
                    onClick={() => handleMove(page.id, "up")}
                    aria-label="Move page up"
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-maroon-700 disabled:opacity-30"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    disabled={index === pages.length - 1 || busyId === page.id}
                    onClick={() => handleMove(page.id, "down")}
                    aria-label="Move page down"
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-maroon-700 disabled:opacity-30"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(page)}
                    aria-label="Delete page"
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this catalog page?"
        description="This removes the page from the public Catalog. This can't be undone."
        confirmLabel="Delete"
        danger
        isBusy={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </AdminLayout>
  );
}
